import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Typography from '@material-ui/core/Typography'
import { useFormContext } from 'react-hook-form'
import ArrowBackIcon from 'components/material-symbol/icons/ArrowBack'
import { PGA_COM_HOST } from 'env'
import { useAuth } from 'lib/auth'
import { composeSteps } from '../utils'
import EventSummaryCard from './event-summary-card'
import ParticipantsStep from './participants-step'
import ReviewStep from './review-step'
import WaiverStep from './waiver-step'
import StepPills from './step-pills'
import { STEP_COPY } from './step-copy'
import useStyles from './styles'

const PARTICIPANTS = 'Participant Information'
const REVIEW = 'Review & Pay'
const WAIVER = 'Waiver'

// Every step stays mounted (hidden when inactive) so form values, the Stripe
// payment element and the waiver submit handler survive step changes, same as
// the original layout.
const RedesignedLayout = ({
  activeStep,
  groupEvent,
  handleNext,
  handlePrev,
  onSubmit,
  marketingOptInChecked,
  handleMarketingOptInChange,
  paymentRequired,
  setPaymentRequired,
  shouldShowWaiver,
  shouldFetchClientSecret,
  setShouldFetchClientSecret,
  onRegistrationFull,
}) => {
  const classes = useStyles()
  const form = useFormContext()
  const { user } = useAuth()
  const stripePaymentFormRef = useRef(null)

  const steps = composeSteps({ shouldShowWaiver, paymentRequired })
  const stepName = steps[activeStep]?.name
  const copy = STEP_COPY[stepName] || {}
  const isOwnerRegistering =
    !!user && user?.externalId === groupEvent?.coach?.externalId

  const hiddenUnless = name => (stepName === name ? undefined : 'none')

  return (
    <div className={classes.page}>
      <div className={classes.inner}>
        {!isOwnerRegistering && (
          <a
            className={classes.backLink}
            href={`${PGA_COM_HOST}/things-to-do/events/${groupEvent.slug}`}
          >
            <ArrowBackIcon aria-hidden="true" />
            Back to event
          </a>
        )}
        <header className={classes.header}>
          <div>
            {steps.length > 1 && (
              <span className={classes.eyebrow}>
                Step {activeStep + 1} of {steps.length}
              </span>
            )}
            <Typography variant="h3" component="h1" className={classes.title}>
              {copy.title}
            </Typography>
          </div>
          {steps.length > 1 && (
            <StepPills steps={steps} activeStep={activeStep} />
          )}
        </header>

        <div className={classes.grid}>
          <div className={classes.mainCol}>
            <div style={{ display: hiddenUnless(PARTICIPANTS) }}>
              <ParticipantsStep
                groupEvent={groupEvent}
                marketingOptInChecked={marketingOptInChecked}
                handleMarketingOptInChange={handleMarketingOptInChange}
                paymentRequired={paymentRequired}
                steps={steps}
                activeStep={activeStep}
                handleNext={handleNext}
              />
            </div>
            <div style={{ display: hiddenUnless(REVIEW) }}>
              <ReviewStep
                groupEvent={groupEvent}
                handleNext={handleNext}
                handlePrev={handlePrev}
                paymentRequired={paymentRequired}
                shouldFetchClientSecret={shouldFetchClientSecret}
                setShouldFetchClientSecret={setShouldFetchClientSecret}
                shouldShowWaiver={shouldShowWaiver}
                stripePaymentFormRef={stripePaymentFormRef}
                marketingOptInChecked={marketingOptInChecked}
                onRegistrationFull={onRegistrationFull}
              />
            </div>
            <div style={{ display: hiddenUnless(WAIVER) }}>
              <WaiverStep
                form={form}
                handlePrev={handlePrev}
                onSubmit={() => {
                  if (stripePaymentFormRef.current?.submitPayment) {
                    return stripePaymentFormRef.current.submitPayment()
                  }
                  return onSubmit()
                }}
              />
            </div>
          </div>
          <aside
            className={clsx(
              classes.sideCol,
              stepName === PARTICIPANTS && classes.sideColFirstOnMobile,
            )}
          >
            <EventSummaryCard
              groupEvent={groupEvent}
              paymentRequired={paymentRequired}
              setPaymentRequired={setPaymentRequired}
            />
          </aside>
        </div>
      </div>
    </div>
  )
}

RedesignedLayout.propTypes = {
  activeStep: PropTypes.number.isRequired,
  groupEvent: PropTypes.object.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrev: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  marketingOptInChecked: PropTypes.bool,
  handleMarketingOptInChange: PropTypes.func,
  paymentRequired: PropTypes.bool,
  setPaymentRequired: PropTypes.func,
  shouldShowWaiver: PropTypes.bool,
  shouldFetchClientSecret: PropTypes.bool,
  setShouldFetchClientSecret: PropTypes.func,
  onRegistrationFull: PropTypes.func,
}

export default RedesignedLayout
